import { omit, stableStringify } from '@/utils/object';
import type { ScheduleStatusFilter } from '../date-bounds';
import {
  appendNextSchedule,
  blankProgram,
  type IndexFeed,
  isOpen,
  type ScheduleStatus,
  scheduleStatus,
  sessionGroupIndices,
  setProgramType,
  splitIntoSessions,
  toDraft,
  toIsoDate,
} from '../mm-program-editing';
import { findSettingsProblems, type MarketMakerInput, type ProgramInput, type ProgramType, SESSIONS } from '../mm-program-schema';
import { type FieldValue, GRID_FIELDS, type GridField } from './grid-fields';

/** One market maker's document as loaded, and as edited in the grid when it has unsaved edits. */
export interface GridDocument {
  pid: string;
  version: number;
  canEdit: boolean;
  saved: MarketMakerInput;
  draft?: MarketMakerInput;
}

export interface ProgramRef {
  pid: string;
  instrument: string;
  /** Position in the instrument's program list, which is the schedule's identity until saved */
  index: number;
}

export type SessionSetPosition = 'first' | 'middle' | 'last';

export interface ProgramRow extends ProgramRef {
  id: string;
  name: string;
  active: boolean;
  program: ProgramInput;
  /** The same schedule as loaded; absent when the document never had one at this position */
  saved?: ProgramInput;
  status: ScheduleStatus;
  /** No edit right: bop-api would refuse a change */
  isReadOnly: boolean;
  /** Where the row sits in its Core, Extended and Weekend set; null for an all-hours schedule */
  sessionSet: SessionSetPosition | null;
}

export const rowIdOf = ({ pid, instrument, index }: ProgramRef) => `${pid}|${instrument}|${index}`;

export const currentOf = (document: GridDocument) => document.draft ?? document.saved;

const dayOf = (value: string) => value.slice(0, 10);

const sessionRank = (program: ProgramInput) => (program.session ? SESSIONS.indexOf(program.session) : -1);

/** Every program of every market maker, by name, instrument, start and session, with C/E/W sets kept together. */
export const buildRows = (documents: GridDocument[], now = new Date()): ProgramRow[] =>
  [...documents]
    .sort((left, right) => currentOf(left).name.localeCompare(currentOf(right).name))
    .flatMap((document) => {
      const current = currentOf(document);
      const programs = current.programs ?? {};
      return Object.keys(programs)
        .sort()
        .flatMap((instrument) => {
          const list = programs[instrument];
          const order = list
            .map((program, index) => ({ program, index }))
            .sort(
              (left, right) =>
                dayOf(left.program.start_date).localeCompare(dayOf(right.program.start_date)) ||
                dayOf(left.program.end_date || '9999').localeCompare(dayOf(right.program.end_date || '9999')) ||
                sessionRank(left.program) - sessionRank(right.program),
            );
          return order.map(({ program, index }): ProgramRow => {
            const saved = document.saved.programs?.[instrument]?.[index];
            const set = program.session ? sessionGroupIndices(list, index) : [];
            const ordered = set.map((position) => order.findIndex((item) => item.index === position)).sort((left, right) => left - right);
            const place = ordered.indexOf(order.findIndex((item) => item.index === index));
            const ref = { pid: document.pid, instrument, index };
            return {
              ...ref,
              id: rowIdOf(ref),
              name: current.name,
              active: current.active === true,
              program,
              saved,
              status: scheduleStatus(program, now),
              isReadOnly: !document.canEdit,
              sessionSet: set.length < 2 ? null : place === 0 ? 'first' : place === ordered.length - 1 ? 'last' : 'middle',
            };
          });
        });
    });

export const fieldValueOf = (row: Pick<ProgramRow, 'program' | 'instrument'>, field: GridField): FieldValue =>
  field.appliesTo(row.program, row.instrument) ? field.get(row.program) : undefined;

/** Whether the cell differs from the loaded schedule; a schedule added since counts as changed throughout. */
export const isCellChanged = (row: ProgramRow, field: GridField) => {
  if (!field.appliesTo(row.program, row.instrument)) return false;
  if (!row.saved) return true;
  const before = field.appliesTo(row.saved, row.instrument) ? field.get(row.saved) : undefined;
  return stableStringify(before) !== stableStringify(field.get(row.program));
};

/**
 * Writes one value into a document. A field the Core, Extended and Weekend schedules must share is written to the whole
 * set, so editing one session never leaves the set disagreeing; bucket widths and sizes stay per session.
 */
export const applyFieldEdit = (document: MarketMakerInput, ref: Omit<ProgramRef, 'pid'>, field: GridField, value: FieldValue): MarketMakerInput => {
  const list = document.programs?.[ref.instrument];
  if (!list?.[ref.index]) return document;
  const targets = field.perSession ? [ref.index] : sessionGroupIndices(list, ref.index);
  const next = list.map((program, index) => (targets.includes(index) && field.appliesTo(program, ref.instrument) ? field.set(program, value) : program));
  return { ...document, programs: { ...document.programs, [ref.instrument]: next } };
};

export interface BulkEditResult {
  documents: Map<string, MarketMakerInput>;
  /** Rows at least one value was written to */
  changedRows: number;
  /** Rows none of the values applied to: read-only, or the fields do not exist on their variant or market */
  skippedRows: number;
}

/** Applies the same values to every selected row, field by field in grid order so a variant change lands before its pay terms. */
export const applyBulkEdit = (rows: ProgramRow[], documents: Map<string, MarketMakerInput>, values: Map<string, FieldValue>): BulkEditResult => {
  const next = new Map(documents);
  const fields = GRID_FIELDS.filter((field) => values.has(field.key));
  let changedRows = 0;
  let skippedRows = 0;
  for (const row of rows) {
    const start = next.get(row.pid);
    if (row.isReadOnly || !start) {
      skippedRows += 1;
      continue;
    }
    let document = start;
    let applied = false;
    for (const field of fields) {
      const program = document.programs?.[row.instrument]?.[row.index];
      if (!program || !field.appliesTo(program, row.instrument)) continue;
      document = applyFieldEdit(document, row, field, values.get(field.key));
      applied = true;
    }
    if (applied) {
      next.set(row.pid, document);
      changedRows += 1;
    } else {
      skippedRows += 1;
    }
  }
  return { documents: next, changedRows, skippedRows };
};

export interface AddSchedulesRequest {
  instruments: string[];
  /** First day, `YYYY-MM-DD` */
  start: string;
  /** Variant of every added schedule; absent keeps a copied schedule's, and starts a new instrument as least performing bucket */
  type?: ProgramType;
  /** Instruments that start with a Core, Extended and Weekend set when new, such as equity futures */
  sessioned: Set<string>;
  /** Each instrument's own index feed, which a new perpetual's volatility ladder is measured on */
  feeds?: Map<string, IndexFeed>;
}

export interface AddSchedulesResult {
  /** The market maker with the schedules added; the same object when none were */
  document: MarketMakerInput;
  /** Schedules added; a Core, Extended and Weekend set counts three */
  added: number;
  /** Open schedules ended on the start day to make way */
  ended: number;
  /** `INSTRUMENT: reason` for each instrument nothing was added to */
  skipped: string[];
}

/**
 * A new instrument's schedule with the default curve and rules, and its widths, sizes and pay left blank, so the grid
 * marks each as missing until it is filled in rather than accepting a zero nobody chose.
 */
const blankSchedule = (start: string, instrument: string, type: ProgramType, feed: IndexFeed | undefined): ProgramInput => {
  const { b1, b2, ...program } = omit(setProgramType(blankProgram(start, instrument, feed), type), 'compensation');
  const blank = (bucket: object) => omit(bucket as { bps: number; kpi: number; compensation?: number }, 'bps', 'kpi', 'compensation');
  return { ...program, b1: blank(b1), b2: blank(b2) } as unknown as ProgramInput;
};

/**
 * Starts a schedule on `start` for each instrument of one market maker. An instrument with schedules gets the next one,
 * as "Start new schedule" in the editor does: open schedules end on the start day and all their terms are copied. A new
 * instrument gets the default curve and rules. Nothing is saved; the schedules land in the draft like any grid edit.
 */
export const addSchedules = (marketMaker: MarketMakerInput, request: AddSchedulesRequest): AddSchedulesResult => {
  const start = toIsoDate(request.start);
  let document = marketMaker;
  let added = 0;
  let ended = 0;
  const skipped: string[] = [];
  for (const instrument of request.instruments) {
    const list = document.programs?.[instrument] ?? [];
    let programs: ProgramInput[];
    if (list.length) {
      const result = appendNextSchedule(list, start);
      if ('error' in result) {
        skipped.push(`${instrument}: ${result.error}`);
        continue;
      }
      const { type } = request;
      programs = type ? result.programs.map((program, index) => (index >= result.index ? setProgramType(program, type) : program)) : result.programs;
      added += programs.length - result.index;
      ended += list.filter(isOpen).length;
    } else {
      const blank = [blankSchedule(start, instrument, request.type ?? 'least_performing_bucket', request.feeds?.get(instrument))];
      programs = request.sessioned.has(instrument) ? splitIntoSessions(blank, 0) : blank;
      added += programs.length;
    }
    document = { ...document, programs: { ...document.programs, [instrument]: programs } };
  }
  return { document, added, ended, skipped };
};

export interface DocumentProblems {
  /** By `rowId|field path` */
  cells: Map<string, string>;
  /** By row id: problems with no column of their own, and ones about the instrument's whole schedule list */
  rows: Map<string, string[]>;
  /** Problems outside the programs, such as the email or downtime */
  document: string[];
  count: number;
}

const FIELD_PATHS = new Set(GRID_FIELDS.map((field) => field.path));
const PROGRAM_PATH = /^programs\.([^[.]+)(?:\[(\d+)\](?:\.(.+))?)?$/;

const pushTo = (map: Map<string, string[]>, key: string, message: string) => map.set(key, [...(map.get(key) ?? []), message]);

/** Validates a document as bop-api will and places each problem on the cell, row or instrument it is about. */
export const locateProblems = (pid: string, document: MarketMakerInput): DocumentProblems => {
  const problems = findSettingsProblems(document);
  const cells = new Map<string, string>();
  const rows = new Map<string, string[]>();
  const rest: string[] = [];
  for (const { path, message } of problems) {
    const match = PROGRAM_PATH.exec(path);
    if (!match) {
      rest.push(path ? `${path}: ${message}` : message);
      continue;
    }
    const [, instrument, position, field] = match;
    if (position === undefined) {
      (document.programs?.[instrument] ?? []).forEach((_, index) => {
        pushTo(rows, rowIdOf({ pid, instrument, index }), message);
      });
      continue;
    }
    const id = rowIdOf({ pid, instrument, index: Number(position) });
    if (field && FIELD_PATHS.has(field)) {
      if (!cells.has(`${id}|${field}`)) cells.set(`${id}|${field}`, message);
    } else {
      pushTo(rows, id, message);
    }
  }
  return { cells, rows, document: rest, count: problems.length };
};

/** Whether the edited document differs from the loaded one, beyond the retired fields every draft drops. */
export const hasEdits = (document: GridDocument) => document.draft !== undefined && stableStringify(document.draft) !== stableStringify(toDraft(document.saved));

export type StatusFilter = ScheduleStatusFilter;

export const STATUS_FILTER_LABELS: Record<StatusFilter, string> = { inForce: 'In force', upcoming: 'Upcoming', ended: 'Ended' };

/** Ended today and scored schedules are both over; the row's own chip still tells them apart. */
export const statusFilterOf = (status: ScheduleStatus): StatusFilter => (status === 'inForce' || status === 'upcoming' ? status : 'ended');

/** Whether a schedule is in force on any day from `from` to `to`, both inclusive UTC days; a blank bound is open. */
export const overlapsPeriod = (program: ProgramInput, from: string, to: string) => {
  const start = dayOf(program.start_date);
  const end = program.end_date.trim() ? dayOf(program.end_date) : null;
  return (!to || start <= to) && (!from || end === null || end > from);
};
